import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Store, CheckCircle, TrendingUp, Users, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';

const SellerLandingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const handleGetStarted = () => {
    if (!isAuthenticated) {
      navigate('/register');
    } else if (user?.user_type === 'seller') {
      navigate('/seller/dashboard');
    } else {
      navigate('/register');
    }
  };

  const benefits = [
    {
      icon: Users,
      title: 'Geniş Müşteri Kitlesi',
      description: 'Türkiye genelinde binlerce potansiyel müşteriye ulaşın'
    },
    {
      icon: Shield,
      title: 'Güvenli Ödeme',
      description: 'Stripe altyapısı ile güvenli ödeme alın'
    },
    {
      icon: TrendingUp,
      title: 'Kolay Yönetim',
      description: 'Kullanıcı dostu panel ile ürün ve siparişlerinizi yönetin'
    }
  ];

  const steps = [
    { step: 1, title: 'Kayıt Olun', description: 'Satıcı hesabı oluşturun' },
    { step: 2, title: 'Mağaza Açın', description: 'Mağaza bilgilerinizi girin' },
    { step: 3, title: 'Ürün Ekleyin', description: 'Ürünlerinizi listeleyin' },
    { step: 4, title: 'Satışa Başlayın', description: 'Siparişleri karşılayın, kazanın' }
  ];

  return (
    <div className="min-h-screen" data-testid="seller-landing-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 px-4">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <Store className="h-10 w-10" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Pazarlo'te <span className="text-orange-400">Satıcı</span> Olun
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              Yedek parçalarınızı Türkiye'nin dört bir yanındaki müşterilere satın. 
              Sadece %15 komisyon ile kazanmaya başlayın.
            </p>
            <Button 
              size="lg" 
              onClick={handleGetStarted}
              className="bg-orange-500 hover:bg-orange-600 text-lg px-8 h-14"
              data-testid="start-selling-btn"
            >
              Hemen Başla
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            Neden Pazarlo?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <Card key={idx} className="text-center p-6">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="h-8 w-8 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Info */}
      <section className="py-16 px-4 bg-orange-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Sadece %15 Komisyon
                </h2>
                <p className="text-slate-600 mb-6">
                  Satışlarınızın %85'ı size ait. Gizli ücret yok, aylık abonelik yok. 
                  Sadece sattığınızda komisyon ödersiniz.
                </p>
                <ul className="space-y-3">
                  {['Ücretsiz mağaza açma', 'Sınırsız ürün listeleme', '7/24 destek', 'Hızlı ödeme aktarımı'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <div className="bg-orange-500 text-white rounded-2xl p-8">
                  <p className="text-6xl font-bold mb-2">%15</p>
                  <p className="text-orange-100">Komisyon Oranı</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            Nasıl Çalışır?
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-slate-900 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Satışa Başlamaya Hazır mısınız?</h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Hemen ücretsiz kayıt olun ve dakikalar içinde mağazanızı açın.
          </p>
          <Button 
            size="lg" 
            onClick={handleGetStarted}
            className="bg-orange-500 hover:bg-orange-600 text-lg px-8 h-14"
          >
            Satıcı Ol
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default SellerLandingPage;
